import React,{useState,useEffect,useRef} from "react";
import {
    Box,
    Popover,
    Typography,
    IconButton,
    Button,
    Badge,
    Grid,
    Tooltip,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemAvatar,
    Avatar,
    DialogContent,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CircleIcon from "@mui/icons-material/Circle";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import { Link } from "react-router-dom";
import { useData,useInterval } from "@pure-ptr/react";
import { getAllNotifications, getUnreadCount, markAllRead, markUnread, markRead,markAllUnread } from "../../store/api/notificationsSubscriptions";
import { Loadable } from "../loadable";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setUnreadCount } from "../../store/actions/notifications";
import { convertDateToLocal } from "../../util/date";
import { infoToastify } from "../../util/toast";
import { NotificationDetailsPopover } from "./NotificationDetailsPopover";
import useStyles from "./NotificationsBar.styles";

export type PopoverMode = 'message' | 'detail';

export interface NotificationPopoverState {
    anchorEl: HTMLElement | null;
    notification: any | null;
    mode: PopoverMode;
}

export const INITIAL_POPOVER_STATE: NotificationPopoverState = {
    anchorEl: null,
    notification: null,
    mode: 'message',
};

export const hypeLinksEnums={fee:"Fee Schedule",rule:"Rule", hosptialservices: "Hospital Services", hospitalservices: "Hospital Services", cdm: "Charge Master", providers: "Providers", client: "Client", claimanalysis: "Claim Analysis"}
export const downloadTypes=["mrf", "claimanalysis","rcm"]

export const createDeepLink = (item, page) => {
    const jsonPayload = JSON.parse(item?.payloadJson || "{}");
    if (page.includes("rule")) {
           return `/cms/contract/${jsonPayload?.Contract?.Id || jsonPayload?.ContractStateId}/rule/${jsonPayload?.ContractServiceId}/edit/${jsonPayload?.Id}/${jsonPayload?.ContractDirectoryId || "root"}/true`;
    }

    if (page?.toLowerCase()?.includes("contract")) {
        return `/cms/contract/view/${jsonPayload?.Id}`;
    }
    if (page?.toLowerCase()?.includes("fee")) {
        return `/cms/feeschedules/edit/-1/${
            jsonPayload?.Id
        }`;
    }
    if(page?.toLowerCase()?.includes("mrf") && jsonPayload?.MrfFileLink){
        return jsonPayload?.MrfFileLink;
    }
    if(page?.toLowerCase()?.includes("rcm") && jsonPayload?.RcmFileLink) {
        return jsonPayload?.RcmFileLink;
    }
    if(page?.toLowerCase()?.includes("claimanalysis") && jsonPayload?.ExportFileLink){
        return jsonPayload?.ExportFileLink;
    }
    if(page?.toLowerCase()?.includes("claimletter") && jsonPayload?.ExportFileLink){
        return jsonPayload?.ExportFileLink;
    }

    return "/";
};

export const getHyperLinkTitle = (item) => item?.split("_")[0].toLowerCase()

const truncateText = (html, limit) => {
    if (!html) return "";
    const text = html.replace(/<[^>]*>?/gm, '');
    if (text.length <= limit) return html;
    return text.substring(0, limit) + "...";
};

const getNotificationsPayload = (clientId: any, currentPage: number, recordsPerPage: number = 10) => ({
    sort: [{ id: 'notification.createdAt', direction: 'desc' }],
    filter: {
        "operands": [
            {
                "operator": "and",
                "filters": [{ "id": "notification.clientId", "equator": "equals", "value": clientId }]
            },
            {
                "operator": "and",
                "filters": [{ "id": "isRead", "equator": "equals", "value": false }]
            }
        ]
    },
    page: { currentPage, recordsPerPage },
});
export const parseJson = (item) => {
        try {
            var json = JSON.parse(item.notification?.payloadJson);
            return json;
        } catch (e) {
            return {};
        }
    };

export default function BasicPopover() {
    const classes = useStyles();
    const dispatch=useDispatch()
    const history = useHistory();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(
        null
    );

    const currentClientId: any = useSelector<RootState>(
    state => state?.session?.user?.preferredDashboardClientId
    );

    const unreadCount = useSelector<RootState>(
        state => (state as any).notifications?.unreadCount
    ) as number;


    const [anchorElOptions, setAnchorElOptions] = useState<HTMLButtonElement | null>(
        null
    );

    const { data: unreadCountData, reload: reloadUnreadCount } = useData(() => getUnreadCount([currentClientId]), [currentClientId]);
    const notificationsCount = (unreadCount as number > 99 ? '99+':unreadCount) ?? 0;

    const [prevUnreadCount, setPrevUnreadCount] = useState<number | null>(null);
    const [isShaking, setIsShaking] = useState(false);

    const [notificationsList, setNotificationsList] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const listRef = React.useRef<any>(null);
    const isInitialLoad = useRef(true);
    
    const [popoverState, setPopoverState] = useState<NotificationPopoverState>(INITIAL_POPOVER_STATE);


    const hoverTimer = useRef<any>(null);
    const popoverPaperRef = useRef<HTMLElement>(null);

    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>, item: any) => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
        // Don't override an open 'detail' popover for the same notification
        if (popoverState.mode === 'detail' && popoverState.notification?.notificationId === item?.notificationId) return;
        const text = item?.notification?.body || getNotificationText(item);
        const textWithoutHtml = text.replace(/<[^>]*>?/gm, '');
        if (textWithoutHtml && textWithoutHtml.length > 250) {
            setPopoverState({ anchorEl: event.currentTarget, notification: item, mode: 'message' });
        }
    };

    const handleMouseLeave = () => {
        if (popoverState.mode === 'detail') return;
        hoverTimer.current = setTimeout(() => {
            setPopoverState(INITIAL_POPOVER_STATE);
        }, 200);
    };

    const handleDetailClick = (e: any, item: any) => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);

        if (
            popoverState.mode === 'detail' &&
            popoverState.notification?.notificationId === item.notificationId
        ) {
            setPopoverState(INITIAL_POPOVER_STATE);
            return;
        }
        setPopoverState({
            anchorEl: popoverPaperRef.current ?? e.currentTarget,
            notification: item,
            mode: 'detail'
        });
    };

    const handlePaperEnter = () => {
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
    };

    const handlePaperLeave = () => {
        if (popoverState.mode === 'detail') return;
        handleMouseLeave();
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

        const {
        data: notificationsPageData,
        isPending: loading,
        reload,
    } = useData(async () => {
        if(!open) return;
        return getAllNotifications(getNotificationsPayload(currentClientId, page));
    }, [page]);



    const [markReadId, setMarkReadId] = useState(null);
    const { isPending: isMarkReadPending } = useData(async () => {
        if (!markReadId) return;
        await markRead([markReadId]);
        setNotificationsList((prev) =>
            prev.map((n) => (n.notificationId === markReadId ? { ...n, isRead: true } : n))
        );
        setMarkReadId(null);
        reloadUnreadCount();
    }, [markReadId]);

    const [markUnreadId, setMarkUnreadId] = useState(null);
    const { isPending: isMarkUnreadPending } = useData(async () => {
        if (!markUnreadId) return;
        await markUnread([markUnreadId]);
        setNotificationsList((prev) =>
            prev.map((n) => (n.notificationId === markUnreadId ? { ...n, isRead: false } : n))
        );
        setMarkUnreadId(null);
        reloadUnreadCount();
    }, [markUnreadId]);

    const [markAllReadTrigger, setMarkAllReadTrigger] = useState(0);
    const { isPending: isMarkAllReadPending } = useData(async () => {
        if (markAllReadTrigger === 0) return;
        await markAllRead([currentClientId]);
        setNotificationsList((prev) =>
            prev.map((item) => ({ ...item, isRead: true }))
        );
        reloadUnreadCount();
    }, [markAllReadTrigger]);

    const [markAllUnreadTrigger, setMarkAllUnreadTrigger] = useState(0);
    const { isPending: isMarkAllUnreadPending } = useData(async () => {
        if (markAllUnreadTrigger === 0) return;
        
        await markAllUnread([currentClientId]);
        if (notificationsList.length === 0) {
            reload();
        } else {
            setNotificationsList((prev) =>
                prev.map((item) => ({ ...item, isRead: false }))
            );
        }
        reloadUnreadCount();
    }, [markAllUnreadTrigger]);


    const handleToggleRead = (item) => {
        if (item.isRead) {
            setMarkUnreadId(item.notificationId);
        } else {
            setMarkReadId(item.notificationId);
        }
    };

    const handleMarkAllRead = () => {
        if (notificationsCount === 0) {
            setMarkAllUnreadTrigger(prev => prev + 1);
        } else {
            setMarkAllReadTrigger(prev => prev + 1);
        }
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setPopoverState(INITIAL_POPOVER_STATE);
        if (hoverTimer.current) clearTimeout(hoverTimer.current);
    };


    

    const IsShowLink = (item) => {
     var json = parseJson(item);
     if(json?.ShowLink != undefined){
        return json?.ShowLink;
     }
     return true;
    }
    const IsShowExternalLink = (item) => {
        if(downloadTypes.includes(getHyperLinkTitle(item.notification.category))) {
            return false;
        }
        var json = parseJson(item);
        if (json?.IsExternalLink != undefined) {
            return json?.IsExternalLink;
        }
        return false;
    };
    const IsShowIcon = (item) => {
     var json = parseJson(item);
     if(json?.ShowIcon != undefined){
        return json?.ShowIcon;
     }
     return false;
    }


    const getNotificationText = (item) => {   
    var json = parseJson(item)
     if(json?.MessageText != undefined){
        return json?.MessageText;
     }
     else if(item?.notification?.detail)
        return item?.notification?.detail;
     return '';
    }

    const handleClickOptions = (event) => {
        setAnchorElOptions(event.currentTarget);
    };

    const handleCloseOptions = () => {
        setAnchorElOptions(null);
    };

    const openHambug = Boolean(anchorElOptions);
    const idHamburg = open ? 'simple-popover-hambburg' : undefined;

    const topOptions = [
        { label: "View All Notifications", breakpoint: 3, onClick: () => {
            handleClose()
            handleCloseOptions()
                history.push("/all-notifications");
            
        } },
        { label: notificationsCount === 0 ? "Mark All As Unread" : "Mark All As Read", breakpoint: 3, onClick: handleMarkAllRead },
    ];

    useEffect(() => {
        if (notificationsPageData?.results) {
            if (page === 1) {
                setNotificationsList(notificationsPageData.results);
            } else {
                setNotificationsList((prev) => {
                    return [...prev, ...notificationsPageData.results];
                });
            }
            setHasMore(notificationsPageData.results.length === 10);
        }
    }, [notificationsPageData]);

    useEffect(() => {
        setPrevUnreadCount(null);
    }, [currentClientId]);

    useEffect(() => {
        if (open) {
            setPage(1);
            setHasMore(true);
            setNotificationsList([]);
            reload();
        }
    }, [open]);

    useEffect(() => {
        if (unreadCountData !== undefined && unreadCountData !== null) {
            dispatch(setUnreadCount(unreadCountData));
   
            if (isInitialLoad.current) {
                setTimeout(() => {
                    isInitialLoad.current = false;
                }, 2000);
            }
        }
    }, [unreadCountData, dispatch]);


    useEffect(() => {
        if (typeof unreadCount === 'number' && unreadCount !== null) {
            if (!isInitialLoad.current && prevUnreadCount !== null && unreadCount > prevUnreadCount) {
                setIsShaking(true);
                setTimeout(() => setIsShaking(false), 500); 

                // Fetch the latest notification to show in toaster
                const fetchLatestAndToast = async () => {
                    try {
                        const response = await getAllNotifications(getNotificationsPayload(currentClientId, 1, 1));

                        if (response?.results?.length > 0) {
                            const latest = response.results[0];
                            const title = latest?.notification?.title || "New Notification";
                            const body = latest?.notification?.body || getNotificationText(latest);
                            
                            infoToastify(
                                <Box>
                                    <Typography variant="subtitle2" className={classes.toasterHeader}>New Notification</Typography>
                                    <Typography variant="subtitle2" className={classes.toasterTitle}>{title}</Typography>
                                    <Typography 
                                        variant="body2" 
                                        className={classes.toasterBody}
                                        dangerouslySetInnerHTML={{ __html: truncateText(body, 150) }}
                                    />
                                </Box>
                            );
                        }
                    } catch (error) {
                        console.error("Failed to fetch latest notification for toast:", error);
                    }
                };

                fetchLatestAndToast();
            }
            setPrevUnreadCount(unreadCount);
        }
    }, [unreadCount, currentClientId, classes]);

    useEffect(() => {
        const unreadVisible = notificationsList.filter(n => !n.isRead).length;
        const isPending = loading || isMarkReadPending || isMarkUnreadPending || isMarkAllReadPending || isMarkAllUnreadPending;
        
        if (open && !isPending && unreadVisible === 0 && unreadCount > 0 && notificationsList.length > 0) {
            if (page !== 1) {
                setPage(1);
            } else {
                reload();
            }
        }
    }, [notificationsList, unreadCount, open, loading, isMarkReadPending, isMarkUnreadPending, isMarkAllReadPending, isMarkAllUnreadPending, page, reload]);

    useInterval(reloadUnreadCount, 60000);
    
    const handleScroll = () => {
        const list = listRef.current;
        if (!list || !hasMore || loading) return;

        if (list.scrollTop + list.clientHeight >= list.scrollHeight - 10) {
            const nextPage = page + 1;
            setPage(nextPage);
        }
    };

    function stringToColor(string) {
        let hash = 0;
        let i;

        /* eslint-disable no-bitwise */
        for (i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }

        let color = "#";

        for (i = 0; i < 3; i += 1) {
            const value = (hash >> (i * 8)) & 0xff;
            color += `00${value.toString(16)}`.slice(-2);
        }

        return color;
    }

    function stringAvatar(name) {
        return {
            sx: {
                bgcolor: stringToColor(name),
            },
            children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
        };
    }

    const renderAvatar = (item) => {
        const category = item?.notification?.category?.toLowerCase() || "";
        const title = item?.notification?.title ?? "Notification";
        const bgcolor = stringToColor(title);

        if (category.includes("contract") || category.includes("rule")) {
            return (
                <Avatar sx={{ bgcolor }}>
                    <i className="far fa-handshake"></i>
                </Avatar>
            );
        } else if (category.includes("fee")) {
            return (
                <Avatar sx={{ bgcolor }}>
                    <i className="flaticon-interface-3"></i>
                </Avatar>
            );
        } else if (category.includes("hosptialservices") || category.includes("hospitalservices")) {
            return (
                <Avatar sx={{ bgcolor }}>
                    <i className="flaticon-medical"></i>
                </Avatar>
            );
        } else if (category.includes("cdm")) {
            return (
                <Avatar sx={{ bgcolor }}>
                    <i className="fas fa-dollar-sign"></i>
                </Avatar>
            );
        } else if (category.includes("providers")) {
            return (
                <Avatar sx={{ bgcolor }}>
                    <i className="fa fa-newspaper"></i>
                </Avatar>
            );
        } else if (category.includes("client")) {
            return (
                <Avatar sx={{ bgcolor }}>
                    <i className="flaticon-rotate"></i>
                </Avatar>
            );
        }
        else if (category.includes("mrf")) {
            return (
                <Avatar sx={{ bgcolor }}>
                    <i className="flaticon-settings"></i>
                </Avatar>
            );
        }

        else if (category.includes("claimanalysis") || category.includes("claim_analysis")) {
            return (
                <Avatar sx={{ bgcolor }}>
                    <i className="flaticon-analytics"></i>
                </Avatar>
            );
        }
        
        else {
            return <Avatar {...stringAvatar(title)} />;
        }
    };

    return (
        <Box className={classes.notificationsContainer}>
            <IconButton
                onClick={handleClick}
                className={isShaking ? "shake" : ""}
            >
                <Badge badgeContent={notificationsCount} color="error">
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                PaperProps={{
                    className: classes.popoverPaper,
                    ref: popoverPaperRef
                }}
            >
                <Loadable
                    loading={
                        isMarkReadPending != null ||
                        isMarkUnreadPending != null ||
                        isMarkAllReadPending != null ||
                        isMarkAllUnreadPending != null
                    }
                >
                    <Grid container className={classes.gridMargin}>
                        <Grid item xs={5} className={classes.alignCenter}>
                            <Typography variant="h6">Notifications</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={6}
                            alignContent={"center"}
                            textAlign={"right"}
                        ></Grid>

                        <Grid
                            item
                            xs={1}
                            textAlign={"right"}
                            alignContent={"center"}
                            alignItems={"right"}
                        >
                            <Button
                                variant="text"
                                aria-describedby={idHamburg}
                                onClick={handleClickOptions}
                                className={classes.hamburgButton}
                            >
                                <MoreVertIcon />{" "}
                            </Button>

                            <Popover
                                id={idHamburg}
                                open={openHambug}
                                anchorEl={anchorElOptions}
                                onClose={handleCloseOptions}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "left",
                                }}
                            >
                                <DialogContent
                                    className={classes.hamburgPopoverContent}
                                >
                                    {topOptions?.map((item) => (
                                        <Typography
                                            className={classes.optionLink}
                                            onClick={() => item?.onClick()}
                                        >
                                            {item?.label}
                                        </Typography>
                                    ))}
                                </DialogContent>{" "}
                            </Popover>
                        </Grid>
                    </Grid>

                    <Divider />

                    {!loading &&
                    (notificationsList?.length === 0 || unreadCount == 0) ? (
                        <Box className={classes.emptyStateContainer}>
                            <Typography>No Notifications Found</Typography>
                        </Box>
                    ) : (
                        <List
                            ref={listRef}
                            onScroll={handleScroll}
                            dense
                            className={classes.notificationsList}
                        >
                            {notificationsList?.map(
                                (item, index) =>
                                    !item?.isRead && (
                                        <ListItem key={item.id} disablePadding>
                                            <ListItemButton>
                                                <ListItemAvatar>
                                                    {renderAvatar(item)}
                                                </ListItemAvatar>

                                                <ListItemText
                                                    primary={
                                                        <Box
                                                            component="span"
                                                            className={
                                                                classes.listItemPrimaryBox
                                                            }
                                                        >
                                                            {item?.notification?.title?.toLowerCase()}
                                                        </Box>
                                                    }
                                                    className={
                                                        classes.listItemText
                                                    }
                                                    secondary={
                                                        <Box
                                                            className={
                                                                classes.listItemSecondaryBox
                                                            }
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                component="span"
                                                                color="text.primary"
                                                                onMouseEnter={(
                                                                    e,
                                                                ) =>
                                                                    handleMouseEnter(
                                                                        e,
                                                                        item,
                                                                    )
                                                                }
                                                                onMouseLeave={
                                                                    handleMouseLeave
                                                                }
                                                                className={
                                                                    classes.notificationBody
                                                                }
                                                                sx={{
                                                                    cursor:
                                                                        (
                                                                            item
                                                                                ?.notification
                                                                                ?.body ||
                                                                            getNotificationText(
                                                                                item,
                                                                            )
                                                                        )
                                                                            .length >
                                                                        250
                                                                            ? "pointer"
                                                                            : "default",
                                                                }}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: truncateText(
                                                                        item
                                                                            ?.notification
                                                                            ?.body ||
                                                                            getNotificationText(
                                                                                item,
                                                                            ),
                                                                        250,
                                                                    ),
                                                                }}
                                                            />
                                                            {IsShowLink(
                                                                item,
                                                            ) && (
                                                                <Link
                                                                    className={
                                                                        classes.reviewLink
                                                                    }
                                                                    onClick={() => {
                                                                        handleClose();
                                                                        handleCloseOptions();
                                                                    }}
                                                                    to={createDeepLink(
                                                                        item?.notification,
                                                                        getHyperLinkTitle(
                                                                            item
                                                                                .notification
                                                                                .category,
                                                                        ),
                                                                    )}
                                                                >
                                                                    Review{" "}
                                                                    {item
                                                                        ?.notification
                                                                        ?.category
                                                                        ? hypeLinksEnums[
                                                                              getHyperLinkTitle(
                                                                                  item
                                                                                      .notification
                                                                                      .category,
                                                                              )
                                                                          ] ||
                                                                          getHyperLinkTitle(
                                                                              item
                                                                                  .notification
                                                                                  .category,
                                                                          )
                                                                        : "Item"}
                                                                </Link>
                                                            )}
                                                            {IsShowExternalLink(
                                                                item,
                                                            ) && (
                                                                <a
                                                                    href={createDeepLink(
                                                                        item?.notification,
                                                                        getHyperLinkTitle(
                                                                            item
                                                                                .notification
                                                                                .category,
                                                                        ),
                                                                    )}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className={
                                                                        classes.reviewLink
                                                                    }
                                                                    onClick={() => {
                                                                        handleClose();
                                                                        handleCloseOptions();
                                                                    }}
                                                                >
                                                                    {downloadTypes.includes(getHyperLinkTitle(item.notification.category)) ? `Download ` : `Open `}
                                                                    {item
                                                                        ?.notification
                                                                        ?.category
                                                                        ? hypeLinksEnums[
                                                                              getHyperLinkTitle(
                                                                                  item
                                                                                      .notification
                                                                                      .category,
                                                                              )
                                                                          ] ||
                                                                          getHyperLinkTitle(
                                                                              item
                                                                                  .notification
                                                                                  .category,
                                                                          )
                                                                        : "Item"}
                                                                </a>
                                                            )}

                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                className={
                                                                    classes.timestamp
                                                                }
                                                            >
                                                                {convertDateToLocal(
                                                                    item
                                                                        ?.notification
                                                                        ?.createdAt,
                                                                )}
                                                            </Typography>
                                                            {item?.notification
                                                                ?.detail && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="primary"
                                                                    sx={{
                                                                        cursor: "pointer",
                                                                        textDecoration: "underline",
                                                                    }}
                                                                    onClick={(e) => handleDetailClick(e, item)}
                                                                >
                                                                    {
                                                                        popoverState.mode === 'detail' &&
                                                                        popoverState.notification?.notificationId === item.notificationId
                                                                            ? "Hide Detail"
                                                                            : "Show Detail"
                                                                    }
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                                {IsShowIcon(item) && (
                                                    <Tooltip
                                                        title={
                                                            item?.notification
                                                                ?.severity ===
                                                            "HIGH"
                                                                ? "Warning"
                                                                : item
                                                                        ?.notification
                                                                        ?.severity ===
                                                                    "CRITICAL"
                                                                  ? "Error"
                                                                  : "Completed"
                                                        }
                                                    >
                                                        <IconButton
                                                            className={
                                                                classes.severityIcon
                                                            }
                                                            onClick={(e) => {}}
                                                        >
                                                            {item?.notification
                                                                ?.severity ===
                                                            "HIGH" ? (
                                                                <WarningIcon
                                                                    className={
                                                                        classes.warningIcon
                                                                    }
                                                                />
                                                            ) : item
                                                                  ?.notification
                                                                  ?.severity ===
                                                              "CRITICAL" ? (
                                                                <ErrorIcon
                                                                    className={
                                                                        classes.errorIcon
                                                                    }
                                                                />
                                                            ) : (
                                                                <CheckCircleIcon
                                                                    className={
                                                                        classes.successIcon
                                                                    }
                                                                />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                <Tooltip
                                                    title={
                                                        item?.isRead
                                                            ? "Mark As Unread"
                                                            : "Mark As Read"
                                                    }
                                                >
                                                    <IconButton
                                                        className={
                                                            classes.readStatusIcon
                                                        }
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleToggleRead(
                                                                item,
                                                            );
                                                        }}
                                                    >
                                                        {!item?.isRead ? (
                                                            <CircleIcon
                                                                color="primary"
                                                                className={
                                                                    classes.statusCircleIcon
                                                                }
                                                            />
                                                        ) : (
                                                            <RadioButtonUncheckedIcon
                                                                color="primary"
                                                                className={
                                                                    classes.statusCircleIcon
                                                                }
                                                            />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            </ListItemButton>
                                        </ListItem>
                                    ),
                            )}

                            {loading != null && hasMore && (
                                <Typography className={classes.loadingText}>
                                    Loading...
                                </Typography>
                            )}
                        </List>
                    )}
                </Loadable>
            </Popover>
            <NotificationDetailsPopover
                popoverState={popoverState}
                handlePaperEnter={handlePaperEnter}
                handlePaperLeave={handlePaperLeave}
                getNotificationText={getNotificationText}
                notificationsPanelRef={popoverPaperRef}
                onClose={() => setPopoverState(INITIAL_POPOVER_STATE)}
            />
        </Box>
    );
}
